import { type SxProps, Typography } from "@mui/material";
import type { FC } from "react";

type Props = {
	text?: string;
	maxChars?: number;
	sxProps?: SxProps;
	render?: "always" | "whenTextExists";
};

export const maxCharsDefault = 1500;

// One character outside the GSM-7 set (emoji, curly quotes, á/í/ó/ú…) makes the whole SMS Unicode, which fits
// ~67 chars per segment; carriers reject it past 10 segments (Twilio 30019). 600 leaves room for the facility
// name the backend prepends.
export const SMS_SPECIAL_CHARS_MAX_CHARS = 600;

// GSM-7 basic table + extension table (\f ^ { } \ [ ~ ] | €).
const GSM7_CHARS = new Set(
	"@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà\f^{}\\[~]|€",
);

/** Unique characters in `text` that force the SMS into Unicode encoding. */
export const getSmsSpecialChars = (text: string): string[] => [...new Set(Array.from(text).filter((char) => !GSM7_CHARS.has(char)))];

export const getSmsMaxChars = (text: string): number => (getSmsSpecialChars(text).length > 0 ? SMS_SPECIAL_CHARS_MAX_CHARS : maxCharsDefault);

export const CharCounter: FC<Props> = ({ maxChars = maxCharsDefault, text, sxProps, render = "whenTextExists" }) => {
	return render === "always" || text?.length ? (
		<Typography
			sx={{
				fontSize: 10,
				color: maxChars && text ? (text.length >= 0.9 * maxChars ? "red" : "grey") : "grey",
				fontWeight: 500,
				opacity: render === "always" && !text ? 0 : 1,
				...sxProps,
			}}
		>
			{text ? text.length : 0}/{maxChars}
		</Typography>
	) : null;
};
